<template>
  <button
    class="base-button"
    :class="[
      emphasisClass,
      themeClass,
      sizeClass,
      fontClass,
      boldClass,
      {
        'base-button--fluid': fluid,
        'base-button--round': round,
      }
    ]"
    data-pendo-ds="everyday_button"
    :data-testid="dataTestid"
    :disabled="disabled"
    :style="{'--ghost-text-open': ghostTextOpen}"
    :type="type"
    @mouseleave="hovering = false"
    @mouseover="hovering = true"
  >
    <span
      class="base-button__content"
      :class="{ 'base-button__content--icon-right': iconRight }"
    >
      <div
        v-if="!!icon || !!loading"
        class="base-button__icon-container"
        :class="iconContainerClass"
      >
        <BaseIcon
          v-if="!loading && !!icon"
          class="base-button__icon"
          :font-size="iconFontSize"
          :icon="`${icon} ${iconStyle}`"
        />
        <BaseIcon
          v-show="loading"
          class="base-button__icon"
          :font-size="iconFontSize"
          icon="fas fa-spinner"
          spin
        />
      </div>
      <!-- styling below for line-height inconsistency -->
      <span
        v-if="!!text"
        class="base-button__text"
        :class="fontClass"
        :data-text="text"
        :style="{'line-height': 'inherit'}"
      >
        {{ text }}
      </span>
    </span>
    <slot />
  </button>
</template>

<script lang="ts">
  import { type PropType, type ButtonHTMLAttributes } from 'vue'
  import BaseIcon from './BaseIcon.vue'

  export default {
    name: 'BaseButton',
    components: {
      BaseIcon,
    },
    props: {
      text: { type: String, required: false, default: '' },
      icon: { type: String, required: false, default: ''  },
      theme: {
        type: String as PropType<'default' | 'mono' | 'destructive' | 'ghost'>,
        required: false,
        default: 'default',
        validator: (prop: string) => ['default', 'mono', 'destructive', 'ghost'].includes(prop),
      },
      emphasis: {
        type: String as PropType<'none' | 'low' | 'regular' | 'high'>,
        required: false,
        default: 'regular',
        validator: (value: string) => ['none', 'low', 'regular', 'high'].includes(value),
      },
      size: {
        type: String as PropType<'comfortable' | 'compact'>,
        default: 'comfortable',
        validator: (value: string) => ['comfortable', 'compact'].includes(value),
      },
      disabled: { type: Boolean, default: false  },
      bold: {
        type: Boolean,
        default: false,
      },
      loading: { type: Boolean, default: false },
      ghostTextOpen: { type: String, default: 'var(--grape-jelly-1000)' },
      dataTestid: {
        type: String,
        default: 'button',
      },
      fluid: {
        type: Boolean,
        default: false,
      },
      type: {
        type: String as PropType<ButtonHTMLAttributes['type']>,
        default: 'submit',
      },
      iconRight: {
        type: Boolean,
        default: false,
      },
      round: {
        type: Boolean,
        default: false,
      },
      iconFontSize: {
        type: String,
        default: 'inherit',
      },
    },
    data() {
      return {
        hovering: false,
      }
    },
    computed: {
      boldClass: function() {
        return this.bold ? 'base-button--bold' : ''
      },
      emphasisClass: function() {
        return this.emphasis ? `base-button--emphasis-${this.emphasis}` : ''
      },
      themeClass: function() {
        return this.theme ? `base-button--theme-${this.theme}` : ''
      },
      sizeClass: function() {
        return this.size ? `base-button--size-${this.size}` : ''
      },
      fontClass: function() {
        return this.size === 'compact' ? 'label-s-regular' : 'label-m-regular'
      },
      iconContainerClass: function() {
        return this.size === 'compact' ? 'base-button__icon-container--size-compact' : ''
      },
      iconClass() {
        return {
          [this.icon]: !!this.icon,
        }
      },
      iconStyle() {
        return (this.hovering || this.bold) && !this.disabled ? 'fa-solid' : 'fa-regular'
      },
    },
  }
</script>

<style scoped lang="scss">
  $theme-colors: (
    "default": "blueberry",
    "mono": "pepper",
    "destructive": "chicago-dog-red"
  );

  .base-button__content {
    display: flex;
    align-items: center;
    justify-content: center;
    gap: var(--space-2);
  }

  .base-button__content--icon-right {
    flex-direction: row-reverse;
  }

  .base-button {
    --button-primary-color: var(--blueberry-1000);
    --button-secondary-color: var(--salt);
    --button-tertiary-color: var(--blueberry-300);

    --button-background-color: var(--button-primary-color);
    --button-color: var(--button-secondary-color);
    --button-border-color: var(--button-primary-color);
    --button-outline-color: var(--blueberry-300);

    --comfortable-height: 1.25rem;
    --compact-height: var(--space-3);

    --ghost-background-default: var(--ghost-pops-15);
    --ghost-background-hover: var(--ghost-pops-35);
    --ghost-background-focus: var(--ghost-pops-35);
    --ghost-background-disabled: var(--ghost-pops-15);
    --ghost-background-open: var(--salt);
    --ghost-text-default: var(--salt);
    --ghost-text-disabled: var(--ghost-pops-15);
    --ghost-border-focus: 2px solid var(--blueberry-300);

    display: flex;
    align-items: center;
    padding: var(--space-2) var(--space-3);
    line-height: var(--comfortable-height);

    border-radius: var(--small, 6px);
    border: 1px solid var(--button-border-color, #0C69DD);
    background: var(--button-background-color, #0C69DD);
    color: var(--button-color, #FFF);

    cursor: pointer;

    &__content {
      display: flex;
      align-items: center;
      gap: var(--space-2);
    }

    @each $theme, $color in $theme-colors {
      &--theme-#{$theme} {
        --button-primary-color: var(--#{$color}-1000);
        --button-secondary-color: var(--salt);
        --button-tertiary-color: var(--#{$color}-200);
      }
    }

    &--emphasis-regular {
      --button-background-color: var(--button-secondary-color);
      --button-color: var(--button-primary-color);
      --button-border-color: var(--button-tertiary-color);
    }

    &--emphasis-low, &--emphasis-none {
      --button-background-color: transparent;
      --button-color: var(--button-primary-color);

      border: 1px solid transparent;
      outline: none;
    }

    &--emphasis-none {
      padding: 0;
    }

    &--size-compact {
      padding: var(--space-1) var(--space-2);
      line-height: var(--compact-height);
    }

    &--emphasis-regular:hover,
    &--emphasis-regular:focus-visible {
      --button-border-color: var(--button-primary-color);
    }

    &--emphasis-low:hover,
    &--emphasis-low:focus-visible {
      font-weight: var(--emphasis-medium);
      box-shadow: var(--cr-ds-medium);
    }

    &--emphasis-none:hover,
    &--emphasis-none:focus {
      font-weight: var(--emphasis-medium);
      box-shadow: none;
    }

    &--open {
      --button-background-color: var(--button-primary-color);
      --button-color: var(--button-secondary-color);
      --button-border-color: var(--button-tertiary-color);

      outline: none;
    }

    &:focus {
      outline: none;
    }

    &:focus-visible {
      outline: 2px solid var(--button-outline-color);
    }

    &:disabled {
      &:not(.base-button--emphasis-low, .base-button--emphasis-none) {
        --button-background-color: var(--pepper-200);
        --button-border-color: var(--pepper-300);

        border: 1px solid var(--button-border-color, #0C69DD);
      }

      --button-color: var(--pepper-500);
    }

    &:hover:disabled {
      cursor: not-allowed;
    }

    &__icon-container {
      display: flex;
      align-items: center;
      justify-content: center;
      height: var(--comfortable-height);
    }

    &__icon-container--size-compact {
      height: var(--compact-height);
    }

    &__icon {
      display: block;
    }

    &--open:hover:not(:disabled) &__icon {
      font-weight: var(--emphasis-low);
    }

    &__icon--size-compact {
      font-size: var(--space-3);
    }

    :hover:not(:disabled) &__icon--size-compact {
      font-size: var(--space-3);
    }

    &:hover:not(:disabled) &__text,
    &--bold:not(disabled) &__text {
      font-weight: var(--emphasis-medium);
    }

    &--open:hover:not(:disabled) &__text {
      font-weight: var(--emphasis-low);
    }

    &__text::after {
      display: block;
      content: attr(data-text);
      font-weight: var(--emphasis-medium);
      height: 0px;
      overflow: hidden;
      visibility: hidden;
    }

    &--theme-ghost {
      --button-background-color: var(--ghost-background-default);
      --button-color: var(--ghost-text-default);
      border-color: var(--ghost-background-default);

      &:hover {
        --button-background-color: var(--ghost-background-hover);
      }

      &:focus {
        --button-background-color: var(--ghost-background-focus);
        border: var(--ghost-border-focus);
      }
    }
  }
  .base-button.base-button--theme-ghost:disabled {
    --button-background-color: var(--ghost-background-disabled);
    --button-color: var(--ghost-text-disabled);
    border: 1px solid var(--ghost-pops-35);
  }
  .base-button--theme-ghost.base-button--open {
    --button-background-color: var(--ghost-background-open);
    --button-color: var(--ghost-text-open, 'var(--grape-jelly-1000)');
  }

  .base-button--round {
    border-radius: var(--cr-br-round);

    &.base-button--size-compact {
      padding: var(--space-1);

      .base-button__icon-container {
        height: 13px;
        width: 13px;
      }

      .base-button__icon {
        font-size: 12px;
      }
    }

    &.base-button--size-comfortable {
      padding: var(--space-2);

      .base-button__icon-container {
        height: 18px;
        width: 18px;
      }

      .base-button__icon {
        font-size: 16px;
      }
    }
  }

  .base-button--fluid {
    width: 100%;
    display: flex;
    justify-content: center;
    align-items: center;
  }

</style>
